/**
 * Where platform-api answers for this client.
 *
 * Its own module rather than a field of the Better Auth client: the inference
 * credential module reaches platform-api too, and the sign-out path reaches back
 * into that module to drop the credential the server holds. Keeping the origin
 * here is what stops those two from importing each other in a circle.
 *
 * `apps/admin/src/shared/api/platform-api-origin.ts` is the same resolution for
 * the operator console.
 */

export const DEFAULT_PLATFORM_API_PORT = 8080;

/**
 * The platform API's base URL, from what the build inlined
 * (`platform-api-build.ts`) and the page's location.
 *
 * - The web address: the page's own origin and the path. The bundle names no
 *   domain, so one image serves any.
 * - An inlined origin: as given.
 * - Neither: the page's own loopback host on the platform API's port. The
 *   session lives in a `SameSite=Lax` cookie on an http gateway, and
 *   `localhost` and `127.0.0.1` are different hosts to a browser: the dev
 *   client on `localhost:5733` and the packaged renderer on `127.0.0.1:3773`
 *   each need their own, and a pinned one makes sign-in answer 200 while
 *   storing no session.
 */
export function resolvePlatformApiBaseUrl(input: {
  readonly path: string | undefined;
  readonly url: string | undefined;
  readonly location: Pick<Location, "origin" | "hostname"> | undefined;
}): string {
  const path = input.path?.trim();
  if (path) {
    if (input.location === undefined) {
      throw new Error("The web address's platform API base needs the page's location.");
    }
    return `${input.location.origin}${path}`;
  }
  const url = input.url?.trim();
  if (url) return url;
  const host =
    input.location === undefined || input.location.hostname.length === 0
      ? "localhost"
      : input.location.hostname;
  return `http://${host}:${DEFAULT_PLATFORM_API_PORT}`;
}

export const platformApiBaseUrl: string = resolvePlatformApiBaseUrl({
  path: import.meta.env.VITE_PLATFORM_API_PATH,
  url: import.meta.env.VITE_PLATFORM_API_URL,
  location: typeof location === "undefined" ? undefined : location,
});
